const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    TIME_OPTIONS.push(`${hh}:${mm}`);
  }
}

const DEFAULT_DAY = { open: "09:00", close: "17:00", closed: false };

function DayToggle({ enabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
        enabled ? "bg-green-600" : "bg-gray-300"
      }`}
      aria-label="Toggle open/closed"
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          enabled ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function TimeSelect({ value, onChange, disabled }) {
  return (
    <select
      value={value || "09:00"}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition ${
        disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"
      }`}
    >
      {TIME_OPTIONS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

/**
 * Determines if a day is open based on the new backend structure.
 * A day is considered open if closed === false (or undefined/null for backwards compat)
 * AND open/close times are set.
 */
function isDayOpen(dayData) {
  if (!dayData) return false;
  // New structure uses explicit closed boolean
  if (dayData.closed === true) return false;
  // Consider open if we have time values
  return dayData.open !== null && dayData.close !== null;
}

export default function OpeningHoursEditor({ value, onChange }) {
  const hours = value || {};

  const handleToggle = (day) => {
    const current = hours[day];
    const isCurrentlyOpen = isDayOpen(current);
    const newHours = { ...hours };

    if (isCurrentlyOpen) {
      // Mark as closed
      newHours[day] = { open: null, close: null, closed: true };
    } else {
      // Mark as open with default times
      newHours[day] = { ...DEFAULT_DAY };
    }

    onChange(newHours);
  };

  const handleTimeChange = (day, field, time) => {
    const current = hours[day] || { ...DEFAULT_DAY };
    const newHours = {
      ...hours,
      [day]: {
        ...current,
        [field]: time,
        closed: false,
      },
    };
    onChange(newHours);
  };

  return (
    <div className="space-y-2">
      {DAYS.map(({ key, label }) => {
        const dayData = hours[key];
        const isOpen = isDayOpen(dayData);

        return (
          <div
            key={key}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2 sm:py-1 border-b border-gray-100 last:border-b-0"
          >
            {/* Row 1 on mobile: Day label + Toggle */}
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <div className="w-24 text-sm font-medium sm:font-normal text-gray-700">{label}</div>
              <DayToggle enabled={isOpen} onToggle={() => handleToggle(key)} />
            </div>

            {/* Row 2 on mobile: Time selects or Closed label */}
            <div className="flex items-center gap-2 text-sm pl-0 sm:pl-0">
              {isOpen ? (
                <>
                  <TimeSelect
                    value={dayData?.open}
                    onChange={(t) => handleTimeChange(key, "open", t)}
                    disabled={false}
                  />
                  <span className="text-gray-500">to</span>
                  <TimeSelect
                    value={dayData?.close}
                    onChange={(t) => handleTimeChange(key, "close", t)}
                    disabled={false}
                  />
                </>
              ) : (
                <span className="text-gray-400 italic">Closed</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
